// The client side of /api/principals and the Agents page's read legs under /api/agents
// (BUILD-BRIEF-R2 §4.1, §13) — owned by the `registry` agent. Every caller goes through
// api/client.ts; no component ever writes a raw fetch.
import { qs, request } from './client.js';
import type { AgentsHealth, Event, ListResponse, Principal, PrincipalCreated, Scope, ToolCall } from '../../shared/types.js';

export function listPrincipals(params: { runs?: boolean } = {}): Promise<ListResponse<Principal>> {
  return request(`/principals${qs({ runs: params.runs ? 1 : null })}`);
}

export interface CreatePrincipalInput {
  name: string;
  kind: Principal['kind'];
  scopes: Scope[];
  rate_per_minute?: number;
  max_concurrency?: number;
  expires_at?: string | null;
}

/** The bearer comes back ONCE, in `token`. There is no route that returns it again. */
export function createPrincipal(input: CreatePrincipalInput): Promise<PrincipalCreated> {
  return request('/principals', { method: 'POST', body: JSON.stringify(input) });
}

export function revokePrincipal(id: number): Promise<Principal> {
  return request(`/principals/${id}/revoke`, { method: 'POST' });
}

/** A fresh token for a live principal — the old one stops working; the new one is shown once. */
export function rotatePrincipal(id: number): Promise<PrincipalCreated> {
  return request(`/principals/${id}/rotate`, { method: 'POST' });
}

export function updatePrincipal(
  id: number,
  patch: Partial<Pick<Principal, 'scopes' | 'rate_per_minute' | 'max_concurrency' | 'expires_at'>>,
): Promise<Principal> {
  return request(`/principals/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export function deletePrincipal(id: number): Promise<void> {
  return request(`/principals/${id}`, { method: 'DELETE' });
}

export function listCalls(params: { principal?: string; outcome?: string; limit?: number } = {}): Promise<ListResponse<ToolCall>> {
  return request(`/principals/calls${qs(params)}`);
}

/** The Agents rail, counted from the ledger, the runs and the proposals tables. */
export interface AgentsSummary {
  runs_24h: number;
  runs_24h_detail: { done: number; failed: number; active: number };
  filed: number;
  filed_detail: { unheld: number; held: number; refused: number };
  pushed_back_7d: number;
  pushed_back_top: { n: number; refuse_reason: string | null; principal: string } | null;
  hermes_24h: number;
  hermes_24h_by_scope: Record<string, number>;
  hermes_24h_refused: number;
  hermes_last_call_at: string | null;
  principals: number;
  principals_live: number;
  send_scope: 'off';
}

export function getAgentsSummary(): Promise<AgentsSummary> {
  return request('/principals/summary');
}

/** The Admin rail (admin only). A leg is null when its producer did not answer — the cell then says "not collected". */
export interface AdminSummary {
  operators: { n: number; admins: number; allowlist: number } | null;
  registration: 'allowlist' | 'open' | null;
  audit_7d: { n: number; total: number } | null;
  settings: { n: number } | null;
  refs: { minted: number; kinds: number; counters: Array<{ kind: string; next: number }> } | null;
}

export function getAdminSummary(): Promise<AdminSummary> {
  return request('/principals/admin-summary');
}

/** The Hermes leg of GET /api/agents/health (§4.4): the local install, read-only, plus the ledger. */
export interface HermesHealth {
  installed: boolean;
  home: string;
  version: string | null;
  config_has_crm: boolean;
  last_call_at: string | null;
  calls_24h: number;
  error?: string | null;
}

export type AgentsHealthWithHermes = AgentsHealth & { hermes?: HermesHealth };

export function getAgentsHealthFull(): Promise<AgentsHealthWithHermes> {
  return request('/agents/health');
}

export interface RegistryCapability {
  name: string;
  description: string;
  readOnly: boolean;
  output: string;
  input: Record<string, unknown>;
}

export interface RegistryGroup {
  scope: Scope;
  capabilities: RegistryCapability[];
  /** The live principals holding this scope. */
  principals: string[];
}

export function getRegistry(): Promise<{ groups: RegistryGroup[]; total: number; send_scope: 'off' }> {
  return request('/agents/registry');
}

export function getAgentEvents(limit = 30): Promise<ListResponse<Event>> {
  return request(`/agents/events${qs({ limit })}`);
}
